<template>
	<v-row class="full-height color-palette-page">
		<v-col cols="12" class="flex-none pb-0">
			<v-card class="color-palette-toolbar-card" flat>
				<v-card-text class="pa-3">
					<v-row align="center" dense>
						<!-- Search -->
						<v-col cols="12" sm="6" md="4" lg="3">
							<v-text-field
								v-model="searchForm.keywords"
								placeholder="Search color palettes..."
								prepend-inner-icon="mdi-magnify"
								outlined
								dense
								hide-details
								clearable
								class="color-palette-search"
								@input="onSearchInput"
								@click:clear="onSearchClear"
							></v-text-field>
						</v-col>

						<!-- Spacer -->
						<v-spacer class="d-none d-md-flex"></v-spacer>

						<!-- New Color Palette Button -->
						<v-col cols="auto" v-if="canManageColorPalettes()">
							<v-btn
								depressed
								rounded
								color="success"
								@click="$router.push('/inventory/color-palette/new')"
							>
								New Color Palette
							</v-btn>
						</v-col>
					</v-row>
				</v-card-text>
			</v-card>
		</v-col>

		<v-col cols="12" class="flex-auto pt-0 color-palette-table-container">
			<v-data-table
				:headers="headers"
				:items="colorPalettes"
				:loading="loading"
				:options.sync="tableOptions"
				:server-items-length="totalCount"
				:footer-props="footerProps"
				:mobile-breakpoint="0"
				class="color-palette-data-table"
				must-sort
				fixed-header
			>
				<!-- Name Column -->
				<template v-slot:item.name="{ item }">
					{{ item.name }}
				</template>

				<!-- Description Column -->
				<template v-slot:item.description="{ item }">
					{{ item.description }}
				</template>

				<!-- Action Column -->
				<template v-slot:item.action="{ item }">
					<v-btn text color="primary" @click.stop="navigateToPalette(item)">
						Available Colors
						<v-icon style="font-size: 1.4rem">mdi-arrow-right</v-icon>
					</v-btn>
				</template>

				<!-- Loading slot -->
				<template v-slot:loading>
					<v-skeleton-loader type="table-row@10"></v-skeleton-loader>
				</template>

				<!-- No data slot -->
				<template v-slot:no-data>
					<div class="text-center pa-4">
						<v-icon large color="grey lighten-1">mdi-palette</v-icon>
						<p class="mt-2 grey--text">No color palettes found</p>
					</div>
				</template>
			</v-data-table>
		</v-col>
	</v-row>
</template>

<script>
import gql from "graphql-tag";
import { hasPermission, SystemPermissions } from "@/models/User";

export default {
	name: "ColorPaletteList",
	data() {
		return {
			loading: true,
			searchForm: {
				keywords: "",
			},
			colorPalettes: [],
			totalCount: 0,
			searchTimeout: null,
			tableOptions: {
				page: 1,
				itemsPerPage: 15,
				sortBy: ["name"],
				sortDesc: [false],
			},
			footerProps: {
				"items-per-page-options": [10, 15, 25, 50],
				"show-first-last-page": true,
			},
			headers: [
				{
					text: "Color Palette Name",
					value: "name",
					sortable: true,
					width: "25%",
				},
				{
					text: "Description",
					value: "description",
					sortable: true,
					width: "55%",
				},
				{
					text: "",
					value: "action",
					sortable: false,
					width: "20%",
					align: "end",
				},
			],
		};
	},
	computed: {
		navigationDirection() {
			return this.$store.getters["globalState/navigationDirection"];
		},
	},
	watch: {
		tableOptions: {
			handler() {
				this.fetchData();
			},
			deep: true,
		},
		navigationDirection: {
			handler(newDirection) {
				if (newDirection) {
					const savedState = this.$store.getters["globalState/colorPaletteState"];
					if (savedState) {
						this.tableOptions.page = Math.floor(savedState.offset / 15) + 1;
						this.searchForm.keywords = savedState.keywords || "";
						if (savedState.sortBy) {
							this.tableOptions.sortBy = [savedState.sortBy];
						}
						if (savedState.sortDesc !== undefined) {
							this.tableOptions.sortDesc = [savedState.sortDesc];
						}

						this.$nextTick(() => {
							if (typeof savedState.scrollPosition === "number") {
								setTimeout(() => {
									window.scrollTo({
										top: savedState.scrollPosition,
										left: 0,
										behavior: "smooth",
									});
								}, 500);
							}
						});
					}
				}
			},
			immediate: true,
		},
	},
	methods: {
		canManageColorPalettes() {
			const user = this.$store.getters.userInfo;
			if (hasPermission(user, SystemPermissions.Inventory_ColorPalette_Create_Update_Delete)) {
				return true;
			}
			return user.isAdmin;
		},

		navigateToPalette(palette) {
			this.$router.push(`/inventory/color-palette/${palette.colorPaletteId}`);
		},

		// Map table column values to GraphQL sort field names
		getSortFieldName(columnValue) {
			const sortFieldMap = {
				name: "name",
				description: "description",
			};
			return sortFieldMap[columnValue] || "name";
		},

		buildQueryVariables() {
			const { page, itemsPerPage, sortBy, sortDesc } = this.tableOptions;
			const keyword = (this.searchForm.keywords || "").trim();

			// Build dynamic sort order
			const sortColumn = sortBy[0] || "name";
			const sortDirection = sortDesc[0] ? "DESC" : "ASC";
			const sortFieldName = this.getSortFieldName(sortColumn);
			const order = [{ [sortFieldName]: sortDirection }];

			return {
				offset: (page - 1) * itemsPerPage,
				take: itemsPerPage,
				order,
				searchTerm: keyword || null,
			};
		},

		async fetchData() {
			this.loading = true;
			try {
				const variables = this.buildQueryVariables();
				const res = await this.$apollo.query({
					query: gql`
						query GetColorPalettes(
							$offset: Int!
							$take: Int!
							$searchTerm: String
							$order: [ColorPaletteSortInput!] = [{ name: ASC }]
						) {
							colorPalettes(
								skip: $offset
								take: $take
								order: $order
								where: { isTemplate: { eq: true } }
								searchTerm: $searchTerm
							) {
								totalCount
								items {
									colorPaletteId
									name
									description
									upcharge
									isTemplate
									colors {
										colorId
										colorPaletteId
										description
										hexCode
										label
										isVisible
										isRetired
										isCustomColor
									}
								}
								pageInfo {
									hasNextPage
									hasPreviousPage
								}
							}
						}
					`,
					variables,
					fetchPolicy: "no-cache",
				});

				this.totalCount = res.data.colorPalettes.totalCount;
				this.colorPalettes = res.data.colorPalettes.items;
				this.loading = false;

				// Auto-navigate if exactly one result is found from a search with exact match
				if (
					this.searchForm.keywords &&
					this.searchForm.keywords.trim() !== "" &&
					this.colorPalettes.length === 1 &&
					(this.searchForm.keywords === this.colorPalettes[0]?.name ||
						this.searchForm.keywords === this.colorPalettes[0]?.description)
				) {
					this.navigateToPalette(this.colorPalettes[0]);
				}
			} catch (error) {
				console.error("Error fetching color palettes:", error);
				this.loading = false;
			}
		},

		onSearchInput() {
			// Debounced search
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}
			this.searchTimeout = setTimeout(() => {
				this.tableOptions.page = 1;
				this.fetchData();
			}, 400);
		},

		onSearchClear() {
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}
			this.tableOptions.page = 1;
			this.fetchData();
		},
	},
	mounted() {
		// Initial fetch will happen via tableOptions watcher
	},
	beforeDestroy() {
		if (this.searchTimeout) {
			clearTimeout(this.searchTimeout);
		}
	},
	beforeRouteLeave(to, from, next) {
		this.$store.dispatch("globalState/saveColorPaletteState", {
			offset: (this.tableOptions.page - 1) * this.tableOptions.itemsPerPage,
			keywords: this.searchForm.keywords,
			sortBy: this.tableOptions.sortBy[0],
			sortDesc: this.tableOptions.sortDesc[0],
			scrollPosition: window.scrollY,
		});
		next();
	},
};
</script>

<style lang="scss" scoped>
.color-palette-page {
	// Use calc to fit within viewport: 100vh - header (64px) - container padding (24px)
	height: calc(100vh - 88px);
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.color-palette-table-container {
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0; // Critical for flex children to respect overflow
	overflow: hidden;
}

.color-palette-toolbar-card {
	border-radius: 4px 4px 0 0 !important;
	border-bottom: 1px solid $color-gray;
	flex-shrink: 0; // Don't shrink the toolbar
}

.color-palette-search {
	::v-deep .v-input__slot {
		background-color: $color-gray-light-2 !important;
	}
}

.color-palette-data-table {
	border-radius: 0 0 4px 4px !important;
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0; // Critical for flex children to respect overflow
	overflow: hidden;

	// Table wrapper should scroll
	::v-deep .v-data-table__wrapper {
		flex: 1;
		overflow-y: auto;
		overflow-x: auto;

		table {
			min-width: 600px;
		}
	}

	// Keep footer fixed at bottom
	::v-deep .v-data-footer {
		flex-shrink: 0;
	}

	// Match existing header styling
	::v-deep > .v-data-table__wrapper > table > thead > tr > th {
		font-size: 1.4rem !important;
		font-weight: 500 !important;
		color: #000 !important;
		text-transform: uppercase;
		background-color: $color-gray-light-2 !important;
		white-space: nowrap;
	}

	// Match existing row height and font size
	::v-deep > .v-data-table__wrapper > table > tbody > tr > td {
		height: 60px !important;
		font-size: 1.6rem !important;
	}

	// Footer styling
	::v-deep .v-data-footer {
		font-size: 1.25rem !important;
	}

	::v-deep .v-data-footer__select .v-select__selections .v-select__selection--comma {
		font-size: 1.15rem !important;
	}
}
</style>
